import { collection, doc, type Reference, subCollection, type Timestamp } from "@doitintl/models-types";

import { type AUTO_GENERATED_AWS_ACCOUNTS_DOC_KEY, type AutoGeneratedAwsAccountsDoc } from "./AutoGeneratedAwsAccounts";
import { type ProductEnum } from "./Collection";
import { type AccountManagersCompany, type CustomerModel, type EarlyAccessFeature } from "./Customer";
import { type Product } from "./Integration";
import { type NotificationsDescriptorDoc } from "./Notifications";
import { type PermissionModel } from "./Permission";
import { type RoleModel } from "./Role";
import { type SupportZendeskTicketModel } from "./Support";
import { type EntitlementKey } from "./Tier";
import { type UserNotification } from "./User";

type AccountRole = { name: string; value: string; vendors: string[] };

export type UserNotificationModel = {
  name: string;
  description: string;
  value: UserNotification;
  order: number;
  permissions?: Reference<PermissionModel>[];
  enabled?: boolean;
};

type UserNotificationDoc = {
  notifications: UserNotificationModel[];
};

export type AwsFeaturePermission = {
  name: string;
  permissions: string[];
  policies: string[] | null;
  textFields?: AwsFeatureTextField[];
  featureFlag?: string;
};

export type AzureFeatureRole = {
  name: string;
  scope: "managementGroup" | "subscription" | "resource";
};

export type AzureFeaturePermission = {
  description: string;
  name: string;
  roles: AzureFeatureRole[];
};

type AwsFeatureTextField = {
  title: string;
  required: boolean;
  label: string;
  placeholder: string;
  helperText: string;
};

export type CloudConnectCategory = {
  allowProjectAccessLevel?: boolean;
  allowDatasetAccessLevel?: boolean;
  description: string;
  featureFlag?: EarlyAccessFeature;
  id: string;
  name: string;
  orgLevelOnlyPermissions?: string[];
  projectLevelOnlyPermissions?: string[];
  datasetLevelOnlyPermissions?: string[];
  outOfScopePermissions?: string[];
  outOfScopePermissionsDescription?: string;
  permissions: string[];
  isDefault?: boolean;
  requiredEntitlements?: EntitlementKey[];
  heritageOnly?: boolean;
  requireWIF?: boolean;
  checkVPCAccess?: boolean;
};

export type GcpLocation = {
  title: string;
  value: string;
};

type CloudConnectDatasetLocations = {
  gcp: GcpLocation[];
};

type CloudConnectDoc = {
  awsFeaturePermissions: AwsFeaturePermission[];
  categories: CloudConnectCategory[];
  datasetLocations: CloudConnectDatasetLocations;
  azureFeaturePermissions: AzureFeaturePermission[];
};

type SlackAppWorkspace = {
  name: string;
  customer: Reference<CustomerModel>;
  botToken: string;
};

type AppModelSlackApp = {
  subCollections: {
    slackAppWorkspaces: SlackAppWorkspace;
  };
};

type AppModelMSTeamsApp = {
  subCollections: {
    channels: MSTeamsChannel;
  };
};

export type MSTeamsChannel = {
  conversationId: string; // the channel id
  channelName: string;

  teamName: string;
  teamId: string;

  tenantId: string;
  serviceUrl: string;

  customerId?: string;
  addedAt?: number;
  isDevBot?: boolean;
};

export type AppUsersCsvColumns = {
  columns: Array<{
    column: string;
    field: string;
  }>;
  notificationCsvLabelsMap: {
    "Cloud Cost Anomalies": string;
    "Cloud Known Issues": string;
    "Cloud Quota Utilization": string;
    "Credits Utilization": string;
    "New Invoices": string;
    "Payment Due/Overdue": string;
  };
};

@collection("app")
export class AppModel {
  docs?: {
    version: AppModelDoc;
    "account-manager-roles": AccountManagerRoles;
    "cloud-connect": CloudConnectDoc;
    fixer: FixerDoc;
    "doit-employees": DoitEmployeesDoc;
    "my-customers": MyCustomersDoc;
    "partner-access": PartnerAccessDoc;
    support: SupportModelDoc;
    stripe: StripePaymentsDoc;
    "priority-v3": PriorityV3;
    "algolia-public": AlgoliaPublicConfig;
    "master-payer-accounts": MasterPayerAccountsDoc;
    [AUTO_GENERATED_AWS_ACCOUNTS_DOC_KEY]: AutoGeneratedAwsAccountsDoc;
    "sso-providers-texts": SsoProvidersTexts;
    userNotifications: UserNotificationDoc;
    notifications: NotificationsDescriptorDoc;
    countries: AppModelCountry;
    perks: PerksConfig;
    "slack-app": AppModelSlackApp;
    "msteams-app": AppModelMSTeamsApp;
    "users-csv-columns": AppUsersCsvColumns;
    "cloudhealth-pricebooks": AppModelCloudHealthPriceBook;
    ava: Ava;
    contracts: AppModelContracts;
    whitelists: WhitelistsDoc;
    entities: AppModelEntities;
  };
}

export type PerkPageConfig = {
  perksType: PerkPageSectionTitles[];
};

type PerkPageSectionTitles = {
  agenda: string;
  checklist: string;
  modules: string;
  id: string;
};

type SsoProvidersTexts = {
  [key in SsoTypes]: {
    [ssoProviderName: string]: SsoLabels;
  };
};

type SsoTypes = "saml" | "oidc";

type SsoLabels = {
  spEntityId: string;
  callbackUrl: string;
  idpLoginUrl: string;
};

export type PartnerData = {
  auth: {
    tenantId: string;
  };
  company: AccountManagersCompany;
  domains: string[];
  name: string;
  allowAuthProvider?: string;
};

@doc("partner-access")
class PartnerAccessDoc {
  partners!: PartnerData[];
}

export type DateStringFormat = `${number}-${number}-${number}`;

@subCollection("doersColumnsConfig")
export class MyCustomersDocDoersColumnsConfigModel {
  columns!: CustomersColumnId[];
}

@subCollection("my-customers")
export class MyCustomersDoc {
  subCollections?: {
    doersColumnsConfig: MyCustomersDocDoersColumnsConfigModel;
  };
}

export type FixerExchangeDailyRatesModel = { [dateFormatted: string]: Record<string, number> };

export type FixerExchangeDailyRatesDoc = {
  docs: { [year: string]: { [dateFormatted: string]: Record<string, number> } };
};

export type FixerExchangeYearlyRatesModel = { [year: string]: FixerExchangeDailyRatesModel };

@doc("fixer")
export class FixerDoc {
  subCollections?: {
    fixerExchangeRates: FixerExchangeDailyRatesDoc;
  };
}

@subCollection("doitRoles")
export class AppModelDoitEmployeesDocDoitRoles {
  roleName!: DoitRole;

  users!: string[];

  description!: string;
}

@subCollection("CustomersMetadata")
export class CustomersMetadata {
  lastUpdated!: Timestamp;

  needsUpdate!: boolean;

  progress?: number;
}

@doc("doit-employees")
class DoitEmployeesDoc {
  subCollections?: {
    doitRoles: AppModelDoitEmployeesDocDoitRoles;
  };
}

@doc("ava")
class Ava {
  subCollections?: {
    customersMetadata: CustomersMetadata;
  };
}

@collection("app")
export class AccountManagerRoles {
  roles!: AccountRole[];
}

@doc("version")
export class AppModelDoc {
  theme?: string;

  commitSha!: string;

  shortCommitSha!: string;

  branch!: string;

  showSnackbar?: boolean;

  timestamp!: Timestamp;

  forceRefresh?: boolean;
}

@collection("app")
export class AppModelCloudHealthPriceBook {
  operations!: {
    name: string;
  }[];

  products!: Product[];

  regions!: {
    name: string;
  }[];
}

export type ContractTypeOption = {
  label: string;
  value: string;
};

@doc("contracts")
export class AppModelContracts {
  options!: ContractTypeOption[];

  awsPpaOptions!: ContractTypeOption[];

  disableEditing!: boolean;

  disabledEditingDate!: Timestamp;

  showDisabledEditingBanner!: boolean;

  subCollections?: {
    "doit-one-time-service-types": OneTimeServiceType;
  };
}

@doc("entities")
export class AppModelEntities {
  disabledStartingDate!: Timestamp;

  showDisabledEditingBanner!: boolean;

  disabledBucketing!: boolean;
}

@doc("countries")
export class AppModelCountry {
  code!: Record<string, Country>;
}

export type Whitelist = {
  domains: string[];
};

@doc("whitelists")
export class WhitelistsDoc {
  troubleshootRedirects!: Whitelist;

  verifyEmailRedirects!: Whitelist;
}

export type Country = {
  name: string;
  region: string;
};

export type ChannelNotificationsProps = {
  customersNotifications: boolean;
  id: string;
  internalChannel?: string;
  name?: string;
  title?: string;
};

@collection("app")
export class AppModelSlack {
  channels!: ChannelNotificationsProps[];
}

export enum CustomersColumnsID {
  Checkbox = "",
  PrimaryDomain = "primaryDomain",
  ThisMonth = "thisMonth",
  Trend = "trend",
  AccountManagers = "accountManagers",
  FlexsaveAWS = "flexsaveAWS",
  BigqueryLens = "bigqueryLens",
  AwsContracts = "awsContracts",
  GcpContracts = "gcpContracts",
  Assets = "assets",
  GkeCostAllocation = "gkeCostAllocation",
  TrialStatus = "trialStatus",
  RevenueEstGCP = "revenueEstGCP",
  Tier = "tier",
}

export type CustomersColumnId = `${CustomersColumnsID}`;

export enum DoitRole {
  AwsAccountGenerator = "aws-account-generator-admin",
  BillingProfileAdmin = "billing-profile-admin",
  CAOwnershipAssigner = "ca-ownership-assigner",
  ContractAdmin = "contract-admin",
  ContractOwner = "contract-owner",
  CustomerSettingsAdmin = "customer-settings-admin",
  CustomerClassificationAdmin = "customer-classification-admin",
  CustomerTieringAdmin = "customer-tiering-admin",
  Developers = "developers",
  FieldSalesManager = "field-sales-manager",
  FlexsaveAdmin = "flexsave-admin",
  FlexsaveSuperAdmin = "flexsave-super-admin",
  MarketplaceAdmin = "marketplace-admin",
  MasterPayerAccountOpsAdmin = "master-payer-account-ops-admin",
  NordiaPreview = "nordia-feature-preview",
  Owners = "owners",
  PerksAdmin = "perks-admin",
  PlatformHouse = "platform-house",
  PreviewAssets = "preview-assets",
  TeamCloudAnalytics = "team-cloud-analytics",
  TemplateLibraryAdmin = "cloud-analytics-template-library-admins",
  UserImpersonator = "user-impersonator",
  InvoicingAdmin = "invoicing-admin",
  InvoicingToolsUser = "invoicing-tools-user",
  InvoicingRecalculationUser = "invoicing-recalculation-user",
  InvoicingCopyBillingTablesUser = "invoicing-copy-billing-tables-user",
  CreateCustomerTenant = "create-customer-tenant",
}

type RoleNotification = {
  name: string;
  roleRef: Reference<RoleModel>;
  notifications: number[];
};

@collection("app")
export class AppRoleNotificationModel {
  roleNotifications!: RoleNotification[];
}

export type AppPartnerData = {
  name: string;
  auth: {
    tenantId: string;
  };
  domains: string[];
  company: "google_cloud_platform" | "amazon_web_services";
  allowAuthProvider?: string;
};

@collection("app")
export class AppPartnerAccessModel {
  partners!: AppPartnerData[];
}

export type SupportTier = "basic" | "business" | "enterprise" | "developer" | "enterprise-on-ramp";
export type SupportModel = "partner-led" | "resold" | null;
export type Tenancy = "dedicated" | "shared";
export type MasterPayerAccountStatus = "pending" | "active" | "retired";
export type BillingProfileStatus = "active" | "inactive";
export type ServiceType = "solve" | "navigator" | "advantage" | "doit-cloud-intelligence";
export type ServiceTier = "essentials" | "standard" | "enhanced" | "premium" | "enterprise" | null;
export type Services = { type: ServiceType; tier: ServiceTier };

export type Support = {
  tier: SupportTier;
  model: SupportModel;
  includeMemberAccounts?: boolean;
  enterpriseTAMLocation?: string;
  supportStart?: Timestamp | null;
  awsCaseIdSupportTicketUrl?: string;
};

export type MasterPayerAccountsFeatures = Partial<{
  "no-root-access": boolean;
  "import-org": boolean;
  "edp-ppa": boolean;
  "no-import-org-reason"?: string;
  "org-billing-start-month"?: Timestamp;
  "nra-expected-mrr"?: number;
  "nra-link-to-materials"?: string;
  "customer-has-cta"?: boolean;
  "is-cur-ingestion-needed"?: boolean;
  "is-cur-ingestion-enabled-per-steps"?: boolean;
  "member-account-s3-bucket-name"?: string;
}>;

export type MpaRevenue = {
  month: string;
  lastUpdated: Timestamp;
  ppa?: number;
  edp?: number;
  support?: number;
  total?: number;
};
@subCollection("mpaAccounts")
export class MasterPayerAccountsModel {
  accountNumber!: string;

  customerId!: string;

  domain?: string;

  name!: string;

  friendlyName!: string;

  rootEmail?: string;

  roleARN!: string;

  tenancyType!: Tenancy;

  flexSaveAllowed?: boolean;

  flexSaveRecalculationStartDate?: Timestamp;

  defaultAwsFlexSaveDiscountRate!: number;

  support!: Support;

  cur_bucket!: string;

  cur_path!: string;

  expectedOnboardingDate?: Timestamp | null;

  status?: MasterPayerAccountStatus; // TO BE MADE MANDATORY FIELDS AFTER CMP-6194

  retireDate?: Timestamp;

  features?: MasterPayerAccountsFeatures; // TO BE MADE MANDATORY FIELDS AFTER CMP-6194

  regions?: string[];

  lastUpdated!: Timestamp;

  timeCreated!: Timestamp;

  requestedBy?: string;

  onboardingTicketId?: string;

  onePasswordItemId?: string;

  requesterLocation?: string;

  ticketAssignee?: string;

  revenues?: MpaRevenue[];

  tags?: string[];

  existingCustomer?: boolean;

  existingCustomerTier?: string;

  services?: Services[];

  removeFromTable?: boolean;
}

@doc("master-payer-accounts")
class MasterPayerAccountsDoc {
  subCollections?: {
    mpaAccounts: MasterPayerAccountsModel;
  };
}

@subCollection("services")
export class SupportServiceModel {
  blacklisted!: boolean;

  last_update!: Timestamp;

  categories!: {
    name: string;
    id: string;
  }[];

  id!: string;

  name!: string;

  platform!: string;

  summary!: string;

  tags!: string[];

  url!: string;

  version!: number;
}

@subCollection("dciAccelerationCustomers")
export class DciAccelerationCustomersModel {
  cohort!: number;

  customerId!: string;
}
@subCollection("zendeskDeduplication")
export class ZendeskDeduplicationModel {
  invocationId!: string;

  timestamp!: Timestamp;
}

class GeckoStatisticsModel {
  docs!: {
    "top-services": SupportStatisticsModel;
  };
}

@doc("support")
export class SupportModelDoc {
  subCollections?: {
    services: SupportServiceModel;
    geckoStatistics: GeckoStatisticsModel;
    plpsCustomers: Record<string, never>;
    dciAccelerationCustomers: DciAccelerationCustomersModel;
    zendeskDeduplication: ZendeskDeduplicationModel;
    tickets: SupportZendeskTicketModel;
  };

  abTestingCounter!: number;
}

export type PriorityStatus = {
  available: boolean;
  scheduledDownTime?: Timestamp | null;
};

export type PriorityKeyValuePair = {
  key: string;
  value: string;
};

export type CompanyInfo = {
  companyId: string;
  companyName: string;
  countries: string[];
  wireTransfers: Record<string, PriorityKeyValuePair[]>;
};

@doc("priority-v3")
export class PriorityV3 {
  companies!: CompanyInfo[];

  status!: PriorityStatus;
}

@doc("stripe")
export class StripePaymentsDoc {
  disable!: {
    acss_debit: boolean;
    bacs_debit: boolean;
    card: boolean;
    sepa_debit: boolean;
    us_bank_account: boolean;
  };

  disable_stripe_payments!: boolean;
}

export type AlgoliaConfig = {
  appId: string;
  searchKey: string;
  devAppId: string;
  devSearchKey: string;
  restrictedIndices: string[];
};

export type AlgoliaPublicConfig = {
  appId: string;
  devAppId: string;
};

@subCollection("statistics")
export class SupportStatisticsModel {
  distribution_products_30d!: {
    label: string;
    value: number;
  }[];
}

export type PerksConfig = {
  perksType: PerkTypeLabel[];
};

export type PerkTypeLabel = {
  id: string;
  agenda: string;
  checklist: string;
  modules: string;
};

export type OneTimeServiceType = {
  id?: string;
  label: string;
  cloud: ProductEnum.GoogleCloud | ProductEnum.AmazonWebServices;
};
